import React, { useMemo, useState } from 'react';
import { MapPin, Trash2, Plus, Clock, Send, CheckCircle2, Route } from 'lucide-react';
import GooglePlacesAddress, { type AddressData } from '../shared/GooglePlacesAddress';
import type { RoutyStopCollectorArtifact } from '../../business/routy/routyArtifacts';
import { parseLocalDateTime, formatDateMedium } from '../../utils/timeUtils';

export interface CollectedStop {
  title: string;
  address: string;
  lat: number | null;
  lng: number | null;
  durationMinutes: number;
}

interface StopCollectorCardProps {
  artifact: RoutyStopCollectorArtifact;
  disabled?: boolean;
  onSubmit: (stops: CollectedStop[], context: { startDate?: string; endDate?: string }) => void;
}

interface Row {
  id: string;
  title: string;
  /** Raw text in the address input (what the user typed/selected). */
  addressInput: string;
  /** Formatted, verified address once a place is picked from autocomplete. */
  fullAddress: string;
  lat: number | null;
  lng: number | null;
  durationMinutes: number;
}

const DURATION_OPTIONS = [15, 30, 45, 60, 90, 120];

let rowSeq = 0;
function nextRowId(): string {
  rowSeq += 1;
  return `stop-row-${Date.now()}-${rowSeq}`;
}

function blankRow(durationMinutes: number): Row {
  return {
    id: nextRowId(),
    title: '',
    addressInput: '',
    fullAddress: '',
    lat: null,
    lng: null,
    durationMinutes,
  };
}

function buildFullAddress(data: AddressData): string {
  const cityState = [data.city, data.state].filter(Boolean).join(', ');
  return [data.address1, cityState, data.zipCode].filter(Boolean).join(', ').trim();
}

function buildInitialRows(artifact: RoutyStopCollectorArtifact): Row[] {
  const rows: Row[] = (artifact.seeds ?? []).map((seed) => ({
    ...blankRow(seed.durationMinutes ?? artifact.defaultDurationMinutes),
    title: seed.title ?? '',
    addressInput: seed.address ?? '',
  }));
  while (rows.length < artifact.count) rows.push(blankRow(artifact.defaultDurationMinutes));
  if (rows.length === 0) rows.push(blankRow(artifact.defaultDurationMinutes));
  return rows;
}

const StopCollectorCard: React.FC<StopCollectorCardProps> = ({ artifact, disabled, onSubmit }) => {
  const [rows, setRows] = useState<Row[]>(() => buildInitialRows(artifact));
  const [submitted, setSubmitted] = useState(false);

  const dateLabel = useMemo(() => {
    if (!artifact.startDate) return null;
    const start = parseLocalDateTime(artifact.startDate);
    if (!start) return null;
    const startLabel = formatDateMedium(start);
    if (artifact.endDate) {
      const end = parseLocalDateTime(artifact.endDate);
      if (end) return `${startLabel} – ${formatDateMedium(end)}`;
    }
    return startLabel;
  }, [artifact.startDate, artifact.endDate]);

  const readyCount = rows.filter((r) => r.addressInput.trim().length > 0).length;
  const lockedDisabled = disabled || submitted;

  const updateRow = (id: string, patch: Partial<Row>) => {
    setRows((prev) => prev.map((r) => (r.id === id ? { ...r, ...patch } : r)));
  };

  const handleAddressSelect = (id: string, data: AddressData) => {
    const full = buildFullAddress(data);
    setRows((prev) =>
      prev.map((r) =>
        r.id === id
          ? {
              ...r,
              addressInput: full || r.addressInput,
              fullAddress: full,
              lat: data.coordinates?.lat ?? null,
              lng: data.coordinates?.lng ?? null,
              // Auto-name a business stop if the user hasn't titled it yet.
              title: r.title.trim() || (data.isEstablishment && data.placeName ? data.placeName : r.title),
            }
          : r,
      ),
    );
  };

  const handleAddressInput = (id: string, value: string) => {
    setRows((prev) =>
      prev.map((r) =>
        r.id === id && value !== r.addressInput
          ? { ...r, addressInput: value, fullAddress: '', lat: null, lng: null }
          : r,
      ),
    );
  };

  const addRow = () => setRows((prev) => [...prev, blankRow(artifact.defaultDurationMinutes)]);
  const removeRow = (id: string) =>
    setRows((prev) => (prev.length > 1 ? prev.filter((r) => r.id !== id) : prev));

  const handleSubmit = () => {
    const stops: CollectedStop[] = rows
      .filter((r) => r.addressInput.trim().length > 0)
      .map((r, i) => ({
        title: r.title.trim() || `Stop ${i + 1}`,
        address: r.fullAddress || r.addressInput.trim(),
        lat: r.lat,
        lng: r.lng,
        durationMinutes: r.durationMinutes,
      }));
    if (!stops.length) return;
    setSubmitted(true);
    onSubmit(stops, { startDate: artifact.startDate, endDate: artifact.endDate });
  };

  return (
    <div className="ml-10 rounded-lg bg-surface border border-ink-200 shadow-sm overflow-hidden">
      {/* Header */}
      <div className="px-4 py-2.5 bg-ink-50 border-b border-ink-200 flex items-center gap-2">
        <Route className="w-4 h-4 text-ink-500" />
        <span className="text-sm font-semibold text-ink-900">Add your stops</span>
        {dateLabel && (
          <span className="ml-auto text-2xs font-medium text-ink-500 bg-surface border border-ink-200 px-2 py-0.5 rounded-md">
            {dateLabel}
          </span>
        )}
      </div>

      {/* Rows */}
      <div className="p-3 space-y-3">
        {rows.map((row, index) => {
          const verified = row.lat != null && row.lng != null;
          return (
            <div key={row.id} className="rounded-lg border border-ink-200 bg-ink-50/40 p-2.5 space-y-2">
              <div className="flex items-center gap-2">
                <span className="flex h-5 w-5 flex-shrink-0 items-center justify-center rounded-md bg-ink-900 text-2xs font-semibold text-white">
                  {index + 1}
                </span>
                <input
                  value={row.title}
                  onChange={(e) => updateRow(row.id, { title: e.target.value })}
                  disabled={lockedDisabled}
                  placeholder="Stop name (optional)"
                  className="flex-1 bg-transparent text-sm font-medium text-ink-800 placeholder:text-ink-400 placeholder:font-normal focus:outline-none disabled:opacity-60"
                />
                {rows.length > 1 && !submitted && (
                  <button
                    onClick={() => removeRow(row.id)}
                    disabled={lockedDisabled}
                    aria-label="Remove stop"
                    className="text-ink-300 hover:text-red-500 transition-colors disabled:opacity-40"
                  >
                    <Trash2 className="w-3.5 h-3.5" />
                  </button>
                )}
              </div>

              <div className="relative">
                <GooglePlacesAddress
                  value={row.addressInput}
                  onChange={(data) => handleAddressSelect(row.id, data)}
                  onInputChange={(value) => handleAddressInput(row.id, value)}
                  placeholder="Search address or business..."
                  searchType="all"
                  enableValidation={false}
                  disabled={lockedDisabled}
                  className="w-full rounded-lg border border-ink-200 bg-surface pl-8 pr-3 py-2 text-sm placeholder:text-ink-400 focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-400 disabled:opacity-60"
                />
                <MapPin
                  className={`pointer-events-none absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 ${
                    verified ? 'text-emerald-500' : 'text-ink-400'
                  }`}
                />
                {verified && (
                  <CheckCircle2 className="pointer-events-none absolute right-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-emerald-500" />
                )}
              </div>

              <div className="flex items-center gap-1.5">
                <Clock className="w-3.5 h-3.5 text-ink-400" />
                <select
                  value={row.durationMinutes}
                  onChange={(e) => updateRow(row.id, { durationMinutes: Number(e.target.value) })}
                  disabled={lockedDisabled}
                  className="bg-surface border border-ink-200 rounded-lg px-2 py-1 text-xs text-ink-700 focus:outline-none focus:ring-2 focus:ring-blue-500/20 disabled:opacity-60"
                >
                  {DURATION_OPTIONS.map((min) => (
                    <option key={min} value={min}>
                      {min} min
                    </option>
                  ))}
                </select>
              </div>
            </div>
          );
        })}

        {!submitted && (
          <button
            onClick={addRow}
            disabled={lockedDisabled}
            className="w-full flex items-center justify-center gap-1.5 py-2 rounded-lg border border-dashed border-ink-300 text-xs font-medium text-ink-500 hover:border-ink-400 hover:text-ink-700 transition-colors disabled:opacity-60"
          >
            <Plus className="w-3.5 h-3.5" />
            Add another stop
          </button>
        )}
      </div>

      {/* Footer */}
      <div className="px-3 pb-3">
        {submitted ? (
          <div className="flex items-center justify-center gap-1.5 py-2 text-xs font-medium text-emerald-600">
            <CheckCircle2 className="w-4 h-4" />
            Sent to Routy
          </div>
        ) : (
          <button
            onClick={handleSubmit}
            disabled={lockedDisabled || readyCount === 0}
            className="w-full flex items-center justify-center gap-2 py-2.5 rounded-lg bg-blue-600 text-white text-sm font-semibold hover:bg-blue-700 active:scale-[0.99] transition-all disabled:opacity-40 disabled:cursor-not-allowed"
          >
            <Send className="w-4 h-4" />
            {readyCount > 0 ? `Routemize ${readyCount} stop${readyCount > 1 ? 's' : ''}` : 'Add an address to continue'}
          </button>
        )}
      </div>
    </div>
  );
};

export default StopCollectorCard;
